import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { SignInRequiredError } from './api/client';
import { msalConfig } from './auth/msalConfig';
import { devMode } from './auth/devMode';
import { startTheme } from './hooks/theme';
import './styles/theme.css';

// Before createRoot, not inside a component: the resolved theme has to be on
// <html> by the time React paints its first frame, or a user who forced dark on a
// light machine gets a white page for one of them. theme.css covers the frame
// before this module runs — see the prefers-color-scheme block at the top of it.
startTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Was retry: 1, and still is for everything else — failureCount is 0 on
      // the first failure. The exception is a sign-in that has expired, which
      // no amount of retrying can fix without the user acting: retrying it only
      // holds the query in isPending, which is the state App.tsx renders as
      // "Loading...", so the card that tells them what happened arrives a
      // backoff later than it should.
      retry: (failureCount, error) => !(error instanceof SignInRequiredError) && failureCount < 1,
      refetchOnWindowFocus: false
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById('root')!);

if (devMode) {
  // No MSAL provider when dev mode is on.
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>
  );
} else {
  const msalInstance = new PublicClientApplication(msalConfig);
  msalInstance.addEventCallback(event => {
    // SSO_SILENT_SUCCESS is the auto sign-in happy path and does not raise
    // LOGIN_SUCCESS, so it has to be handled here too or the active account
    // stays unset for everyone who never sees a redirect.
    const signedIn =
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS;
    if (signedIn && event.payload && 'account' in event.payload) {
      msalInstance.setActiveAccount(
        (event.payload as { account: import('@azure/msal-browser').AccountInfo }).account
      );
    }
  });

  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>
  );
}
